// Footer partagé sur toutes les pages publiques de Revyo.
// Ne pas monter dans Dashboard/QRCode/Configuration (utilisateurs loggés).
//
// i18n : ce composant est le PREMIER à passer en multi-langues (test end-to-end
// du chantier feat/i18n-foundation). Toutes les strings sont dans le namespace
// 'footer' (frontend/src/i18n/locales/{fr,en,es,it}/footer.json).
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
// LanguageSwitcher déplacé ici depuis la top-bar (PR feat/landing-scroll-anims-footer-lang).
// Position bottomBar = discrète mais accessible, conforme aux conventions e-commerce/SaaS.
import LanguageSwitcher from './LanguageSwitcher/LanguageSwitcher';

const CURRENT_YEAR = new Date().getFullYear();

const styles = {
  footer: {
    background: '#1A1A1A',
    color: '#EDEAE3',
    padding: '48px 24px 24px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Inter", sans-serif',
    marginTop: 'auto'
  },
  inner: {
    maxWidth: 1200,
    margin: '0 auto',
    display: 'grid',
    gridTemplateColumns: '1.3fr 1fr 1fr 1fr',
    gap: 40,
    paddingBottom: 32,
    borderBottom: '1px solid rgba(237, 234, 227, 0.12)'
  },
  brand: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12
  },
  brandRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10
  },
  logoSquare: {
    width: 36,
    height: 36,
    background: '#E8502A',
    color: '#FFFFFF',
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 800,
    fontSize: 20
  },
  logoText: {
    fontWeight: 700,
    fontSize: 20
  },
  baseline: {
    fontSize: 13,
    color: 'rgba(237, 234, 227, 0.65)',
    lineHeight: 1.5,
    maxWidth: 280
  },
  colTitle: {
    fontSize: 11,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    color: 'rgba(237, 234, 227, 0.55)',
    marginBottom: 14
  },
  linkList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10
  },
  link: {
    color: '#EDEAE3',
    textDecoration: 'none',
    fontSize: 13,
    transition: 'color 0.15s'
  },
  bottomBar: {
    maxWidth: 1200,
    margin: '0 auto',
    paddingTop: 24,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 12,
    fontSize: 12,
    color: 'rgba(237, 234, 227, 0.55)'
  },
  // Wrapper du LanguageSwitcher : centre l'élément dans la bottomBar.
  // Sur mobile (flex-wrap), il prend sa propre ligne au milieu, touch target ≥44×44px
  // garanti par le composant LanguageSwitcher lui-même (variante compact).
  langWrap: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 44
  },
  // Bloc « Suivez-nous » : rangée d'icônes réseaux sociaux sous la baseline.
  socialRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    marginTop: 4,
    flexWrap: 'wrap'
  },
  // Cible tactile confortable (~42px) ; monochrome au repos, accent orange au survol/focus.
  socialLink: {
    width: 42,
    height: 42,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 8,
    color: 'rgba(237, 234, 227, 0.7)',
    transition: 'color 0.15s ease, transform 0.15s ease',
    outlineOffset: 2
  }
};

// Réseaux sociaux officiels Revyo. Icônes = SVG inline (pas de dépendance).
// aria-label localisé via t('social_follow_*'), svg décoratif => aria-hidden.
const SOCIALS = [
  {
    key: 'instagram',
    href: 'https://www.instagram.com/getrevyo/',
    path: (
      <>
        <path d="M12 2.16c3.2 0 3.58.01 4.85.07 1.17.05 1.8.25 2.23.41.56.22.96.48 1.38.9.42.42.68.82.9 1.38.16.42.36 1.06.41 2.23.06 1.27.07 1.65.07 4.85s-.01 3.58-.07 4.85c-.05 1.17-.25 1.8-.41 2.23-.22.56-.48.96-.9 1.38-.42.42-.82.68-1.38.9-.42.16-1.06.36-2.23.41-1.27.06-1.65.07-4.85.07s-3.58-.01-4.85-.07c-1.17-.05-1.8-.25-2.23-.41-.56-.22-.96-.48-1.38-.9-.42-.42-.68-.82-.9-1.38-.16-.42-.36-1.06-.41-2.23-.06-1.27-.07-1.65-.07-4.85s.01-3.58.07-4.85c.05-1.17.25-1.8.41-2.23.22-.56.48-.96.9-1.38.42-.42.82-.68 1.38-.9.42-.16 1.06-.36 2.23-.41 1.27-.06 1.65-.07 4.85-.07M12 0C8.74 0 8.33.01 7.05.07 5.78.13 4.9.33 4.14.63c-.79.31-1.46.72-2.13 1.38C1.35 2.68.94 3.35.63 4.14.33 4.9.13 5.78.07 7.05.01 8.33 0 8.74 0 12s.01 3.67.07 4.95c.06 1.27.26 2.15.56 2.91.31.79.72 1.46 1.38 2.13.67.66 1.34 1.07 2.13 1.38.76.3 1.64.5 2.91.56C8.33 23.99 8.74 24 12 24s3.67-.01 4.95-.07c1.27-.06 2.15-.26 2.91-.56.79-.31 1.46-.72 2.13-1.38.66-.67 1.07-1.34 1.38-2.13.3-.76.5-1.64.56-2.91.06-1.28.07-1.69.07-4.95s-.01-3.67-.07-4.95c-.06-1.27-.26-2.15-.56-2.91-.31-.79-.72-1.46-1.38-2.13C21.32 1.35 20.65.94 19.86.63 19.1.33 18.22.13 16.95.07 15.67.01 15.26 0 12 0z" />
        <path d="M12 5.84A6.16 6.16 0 1 0 18.16 12 6.16 6.16 0 0 0 12 5.84zm0 10.16A4 4 0 1 1 16 12a4 4 0 0 1-4 4z" />
        <circle cx="18.41" cy="5.59" r="1.44" />
      </>
    )
  },
  {
    key: 'facebook',
    href: 'https://www.facebook.com/profile.php?id=61591804131923',
    path: <path d="M24 12.07C24 5.4 18.63 0 12 0S0 5.4 0 12.07C0 18.1 4.39 23.1 10.13 24v-8.44H7.08v-3.49h3.05V9.41c0-3.02 1.79-4.69 4.53-4.69 1.31 0 2.68.24 2.68.24v2.97h-1.51c-1.49 0-1.96.93-1.96 1.89v2.25h3.33l-.53 3.49h-2.8V24C19.61 23.1 24 18.1 24 12.07z" />
  },
  {
    key: 'x',
    href: 'https://x.com/GetRevyo',
    path: <path d="M18.9 1.15h3.68l-8.04 9.19L24 22.85h-7.41l-5.8-7.58-6.64 7.58H.46l8.6-9.83L0 1.15h7.59l5.24 6.93 6.06-6.93zm-1.29 19.5h2.04L6.48 3.24H4.29L17.6 20.65z" />
  },
  {
    key: 'linkedin',
    href: 'https://www.linkedin.com/company/getrevyo/',
    path: <path d="M20.45 20.45h-3.56v-5.57c0-1.33-.02-3.04-1.85-3.04-1.85 0-2.14 1.45-2.14 2.94v5.67H9.35V9h3.42v1.56h.05c.48-.9 1.64-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.46v6.28zM5.34 7.43a2.06 2.06 0 1 1 0-4.13 2.06 2.06 0 0 1 0 4.13zM7.12 20.45H3.56V9h3.56v11.45zM22.22 0H1.77C.8 0 0 .78 0 1.75v20.5C0 23.22.8 24 1.77 24h20.45c.98 0 1.78-.78 1.78-1.75V1.75C24 .78 23.2 0 22.22 0z" />
  }
];

// Icône sociale : gère hover/focus-visible en JS (styles inline, pas de CSS module ici).
function SocialLink({ href, label, children }) {
  const [active, setActive] = useState(false);
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      style={{
        ...styles.socialLink,
        ...(active ? { color: '#E8502A', transform: 'scale(1.12)' } : {})
      }}
      onMouseEnter={() => setActive(true)}
      onMouseLeave={() => setActive(false)}
      onFocus={() => setActive(true)}
      onBlur={() => setActive(false)}
    >
      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        {children}
      </svg>
    </a>
  );
}

export default function Footer() {
  const { t } = useTranslation('footer');
  return (
    <footer style={styles.footer}>
      <div style={{
        ...styles.inner,
        ...(typeof window !== 'undefined' && window.innerWidth < 768
          ? { gridTemplateColumns: '1fr', gap: 32 }
          : {})
      }}>
        <div style={styles.brand}>
          <div style={styles.brandRow}>
            <div style={styles.logoSquare}>R</div>
            <span style={styles.logoText}>Revyo</span>
          </div>
          <div style={styles.baseline}>
            {t('baseline')}
          </div>
          <div style={styles.socialRow}>
            {SOCIALS.map(({ key, href, path }) => (
              <SocialLink key={key} href={href} label={t(`social_follow_${key}`)}>
                {path}
              </SocialLink>
            ))}
          </div>
        </div>

        <div>
          <div style={styles.colTitle}>{t('col_product_title')}</div>
          <div style={styles.linkList}>
            <Link to="/comment-ca-marche" style={styles.link}>{t('col_product_features')}</Link>
            <Link to="/tarifs" style={styles.link}>{t('col_product_pricing')}</Link>
            <Link to="/blog" style={styles.link}>{t('col_product_blog')}</Link>
            <Link to="/score-rps" style={styles.link}>{t('col_product_rps')}</Link>
          </div>
        </div>

        <div>
          <div style={styles.colTitle}>{t('col_legal_title')}</div>
          <div style={styles.linkList}>
            <Link to="/mentions-legales" style={styles.link}>{t('col_legal_mentions')}</Link>
            <Link to="/cgv" style={styles.link}>{t('col_legal_cgv')}</Link>
            <Link to="/politique-de-confidentialite" style={styles.link}>{t('col_legal_privacy')}</Link>
          </div>
        </div>

        <div>
          <div style={styles.colTitle}>{t('col_contact_title')}</div>
          <div style={styles.linkList}>
            <Link to="/contact" style={styles.link}>{t('col_contact_write')}</Link>
            <a href="mailto:contact@getrevyo.com" style={styles.link}>contact@getrevyo.com</a>
            <span style={{ ...styles.link, opacity: 0.7 }}>{t('col_contact_city')}</span>
          </div>
        </div>
      </div>

      <div style={styles.bottomBar}>
        <div>{t('bottom_copyright', { year: CURRENT_YEAR })}</div>
        <div style={styles.langWrap}>
          <LanguageSwitcher compact />
        </div>
        <div>{t('bottom_trademark')}</div>
      </div>
    </footer>
  );
}
